import React, { useState } from "react";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";

import { MIN_COLUMN_SPAN } from "../../../constants/org-dashboard";

export interface SplitMenuProps {
  column: number;
  item: number;
  widths: [number, number];
  onResize: (column: number, item: number, firstWidth: number) => void;
  onRemove: (column: number, item: number) => void;
}

/**
 * A split's own controls: how its two sides divide the column, and a way to
 * fold it back into plain stacked cards.
 */
const SplitMenu = ({
  column,
  item,
  widths,
  onResize,
  onRemove,
}: SplitMenuProps) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = () => setAnchor(null);

  const total = widths[0] + widths[1];
  const ratios: number[] = [];
  for (let first = MIN_COLUMN_SPAN; first <= total - MIN_COLUMN_SPAN; first++) {
    ratios.push(first);
  }

  return (
    <>
      <Tooltip title="Split layout options">
        <IconButton
          size="small"
          onClick={(event) => setAnchor(event.currentTarget)}
          aria-label="Split layout options"
          data-testid={`dashboard-split-menu-${column}-${item}`}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        <Box sx={{ px: 2, pt: 1, pb: 1.5 }}>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block", mb: 0.75 }}
          >
            {`Ratio of ${total} columns`}
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={widths[0]}
            onChange={(_event, next) => {
              if (next) onResize(column, item, Number(next));
            }}
            aria-label="Split ratio"
          >
            {ratios.map((first) => (
              <ToggleButton key={first} value={first} sx={{ px: 1 }}>
                {`${first}:${total - first}`}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Divider />
        <MenuItem
          onClick={() => {
            onRemove(column, item);
            close();
          }}
        >
          <ListItemText sx={{ color: "error.main" }}>Remove split</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default SplitMenu;
